import { Table, TableBody, TableContainer } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EMPTY_STRING } from "../../../common/constants/common.constants";
import { CLUB_DETAILS } from "../../../common/constants/routes.constants";
import { ApplicationsInvitations } from "../../../common/enums/common.enums";
import { getTrimmedPublicKey } from "../../../utilities/helpers";
import ClubTableCell from "../ClubTableCell/ClubTableCell";
import DashboardTableCell from "../DashboardTable/DashboardTableCell/DashboardTableCell";
import DashboardTableRow from "../DashboardTable/DashboardTableRow/DashboardTableRow";
import TableHeading from "../DashboardTable/TableHeading/TableHeading";
import { tableWrapper } from "../DashboardTable/TableHeading/TableHeading.styles";
import RoleTableCell from "../RoleTableCell/RoleTableCell";
import InvitationsTab from "./InvitationsTab/InvitationsTab";
import invitationsIcon from "../../../assets/dashboardInvitations.svg";
import "./InvitationsTable.scss";
import { dashboardStore } from "../../../contexts/dashboardStore";
import TableInfo from "../TableInfo/TableInfo";

const InvitationsTable = () => {
  useEffect(() => {
    getApplicationsAndInvitations();
  }, []);

  const [activeOption, setActiveOption] = useState<ApplicationsInvitations>(
    ApplicationsInvitations.Applications
  );
  const { clubs, applications, invitations } = dashboardStore();

  const [filteredInvitations, setFilteredInvitations] = useState(invitations);
  const [filteredApplications, setFilteredApplications] =
    useState(applications);
  const [cardDetails, setCardDetails] =
    useState<{ title: string; amount: string | number }[]>();

  const getApplicationsAndInvitations = () => {
    const applicationsAmount = applications!.length;
    const invitationsAmount = invitations!.length;
    setCardDetails([
      { amount: applicationsAmount, title: "Applications" },
      { amount: invitationsAmount, title: "Invitations" },
    ]);
  };

  const renderInvitations = useMemo(() => {
    return filteredInvitations?.map((club) => {
      return (
        <DashboardTableRow
          redirectUrl={`${CLUB_DETAILS}/${club.club.clubAddress}`}
          key={club.club.clubAddress}
        >
          <ClubTableCell
            clubImageUrl={club.club.clubImageUrl}
            clubName={club.club.clubName}
          />
          <DashboardTableCell value={getTrimmedPublicKey(club.clubAuthority)} />
          <RoleTableCell role={club.role} />
        </DashboardTableRow>
      );
    });
  }, [clubs, filteredInvitations]);

  const renderApplications = useMemo(() => {
    return filteredApplications!.map((application) => {
      return (
        <DashboardTableRow
          redirectUrl={`${CLUB_DETAILS}/${application.clubAddress}`}
          key={application.clubAddress}
        >
          <ClubTableCell
            clubImageUrl={application.clubImage}
            clubName={application.clubName}
          />
          <DashboardTableCell value={application?.date} />
        </DashboardTableRow>
      );
    });
  }, [filteredApplications]);

  const filterByName = useCallback(
    (name: string) => {
      if (activeOption === ApplicationsInvitations.Applications) {
        if (name === EMPTY_STRING) {
          setFilteredApplications(applications);
        }
        setFilteredApplications(
          applications!.filter((app) => app.clubName.startsWith(name))
        );
      }
      if (activeOption === ApplicationsInvitations.Invitations) {
        if (name === EMPTY_STRING) {
          setFilteredInvitations(invitations);
          return;
        }
        setFilteredInvitations(
          invitations!.filter((inv) => inv.club.clubName.startsWith(name))
        );
      }
    },
    [applications, invitations, activeOption]
  );

  return (
    <div className="invitations-table">
      <TableInfo
        title="Pending requests"
        cardDetails={cardDetails}
        icon={invitationsIcon}
        description={`${applications!.length + invitations!.length}`}
      />
      <div className="invitations-table__buttons">
        <InvitationsTab
          title="Applications"
          step={ApplicationsInvitations.Applications}
          isActive={activeOption === ApplicationsInvitations.Applications}
          amount={applications?.length.toString()}
          handleClick={(step: ApplicationsInvitations) => setActiveOption(step)}
        />
        <InvitationsTab
          title="Invitations"
          step={ApplicationsInvitations.Invitations}
          isActive={activeOption === ApplicationsInvitations.Invitations}
          amount={invitations?.length.toString()}
          handleClick={(step: ApplicationsInvitations) => setActiveOption(step)}
        />
      </div>
      <TableContainer style={tableWrapper}>
        <Table>
          {activeOption === ApplicationsInvitations.Invitations && (
            <>
              <TableHeading
                showInputField
                onInput={(e) => {
                  filterByName(e.target.value);
                }}
                columns={["Invited by", "For role"]}
              />
              <TableBody>{renderInvitations}</TableBody>
            </>
          )}
          {activeOption === ApplicationsInvitations.Applications && (
            <>
              <TableHeading
                columns={["Date application sent"]}
                showInputField
                onInput={(e) => {
                  filterByName(e.target.value);
                }}
              />
              <TableBody>{renderApplications}</TableBody>
            </>
          )}
        </Table>
      </TableContainer>
    </div>
  );
};

export default InvitationsTable;
